import React, { Children, useMemo } from 'react';
import AdContainer from '../../../../../components/ads';
import { useResizeObserver } from '../../../../../hooks/resize-observer';
import * as S from './styles';

type Props = {
  children: React.ReactNode;
  numOfColumns: number;
};

/**
 * Defines the fallback height for the ad row, which is used e.g. if ResizeObserver isn't available
 * in the browser. This height is the standard in the Swedish market (120px + 2*1px border), but it
 * varies between markets, so we use the ResizeObserver approach to get the real height.
 */
const DEFAULT_AD_ROW_HEIGHT = 122;

const MIN_ROW_TO_SHOW_AD = 10;
const INDEX_FOR_AD = 2;

export const ContainerAdsTable = ({ children, numOfColumns }: Props) => {
  const shouldShowAd = Children.count(children) >= MIN_ROW_TO_SHOW_AD;
  const { height: adRowHeight = DEFAULT_AD_ROW_HEIGHT, ref: stickyAdRowRef } =
    useResizeObserver();
  const adTdStyle = useMemo(() => ({ height: adRowHeight }), [adRowHeight]);

  const childrenWithAd = useMemo<React.ReactNode>(() => {
    const adRow = (
      <S.AdTr key={`panorama_ad_${INDEX_FOR_AD}`}>
        <S.AdTd colSpan={numOfColumns} style={adTdStyle}>
          <S.StickyAdRow ref={stickyAdRowRef}>
            <AdContainer
              scalable
              scaleBase=".pj-ui-panel"
              scaleOrigin="left center"
              placement="panorama_extra"
              format="panorama_1_wde"
              min={861}
              max={10000}
            />
          </S.StickyAdRow>
        </S.AdTd>
      </S.AdTr>
    );

    const arr = React.Children.toArray(children);

    return [...arr.slice(0, INDEX_FOR_AD), adRow, ...arr.slice(INDEX_FOR_AD)];
  }, [children, adTdStyle, numOfColumns, stickyAdRowRef]);

  return <>{shouldShowAd ? childrenWithAd : children}</>;
};
